import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import DashboardLayout from '../../components/layouts/DashboardLayout';
import useInitialData from '../../hooks/useInitialData';
import { useDashboardData } from '../../hooks/useDashboardData';
import TestNotification from '../../components/test/TestNotification';
import { 
  Vote, 
  Package, 
  MessageSquare, 
  Bell, 
  Users, 
  TrendingUp,
  Activity,
  AlertCircle,
  Loader2,
  Calendar
} from 'lucide-react';

const formatTimeAgo = (date: Date): string => {
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  
  if (diffInSeconds < 60) return 'Just now';
  
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
  
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours}h ago`;
  
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) return `${diffInDays}d ago`;
  
  return date.toLocaleDateString();
};

const getActivityIcon = (type: string) => {
  switch (type) {
    case 'decision':
      return <Vote className="h-4 w-4 text-blue-500" />;
    case 'product':
      return <Package className="h-4 w-4 text-green-500" />;
    case 'announcement':
      return <Bell className="h-4 w-4 text-orange-500" />;
    case 'discussion':
      return <MessageSquare className="h-4 w-4 text-purple-500" />;
    default:
      return <Activity className="h-4 w-4 text-gray-500" />;
  }
};

const Dashboard: React.FC = () => {
  const { userProfile } = useAuth();
  const isAdmin = userProfile?.role === 'admin';
  
  // Initialize sample data for first-time admin users
  useInitialData();
  
  // Fetch real data
  const { stats, recentActivities, loading, error } = useDashboardData(
    userProfile?.uid || '',
    isAdmin
  );

  const quickActions = [
    {
      name: 'Vote on Decisions',
      description: 'Participate in company decisions',
      href: '/decisions',
      icon: Vote,
      color: 'bg-blue-500'
    },
    {
      name: isAdmin ? 'Manage Products' : 'Add Product',
      description: isAdmin ? 'View and manage all products' : 'List a new product',
      href: '/team-products',
      icon: Package,
      color: 'bg-green-500'
    },
    {
      name: 'Team Chat',
      description: 'Join the discussion',
      href: '/discussion',
      icon: MessageSquare,
      color: 'bg-purple-500'
    },
    {
      name: 'View Announcements',
      description: 'Check latest updates',
      href: '/announcements',
      icon: Bell,
      color: 'bg-orange-500'
    }
  ];

  const statsData = useMemo(() => [
    {
      name: 'Active Decisions',
      value: stats.activeDecisions.toString(),
      icon: Vote,
      color: 'text-blue-600'
    },
    {
      name: isAdmin ? 'All Products' : 'My Products',
      value: stats.productsListed.toString(),
      icon: Package,
      color: 'text-green-600'
    },
    {
      name: 'Team Members',
      value: stats.teamMembers.toString(),
      icon: Users,
      color: 'text-purple-600'
    },
    {
      name: 'Monthly Growth',
      value: stats.monthlyGrowth,
      icon: TrendingUp,
      color: 'text-orange-600'
    }
  ], [stats, isAdmin]);

  return (
    <DashboardLayout>
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-gray-900">
              Welcome back, {userProfile?.displayName || 'Team Member'}!
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              Here's what's happening with your team today.
            </p>
          </div>

          {/* Stats */}
          {loading ? (
            <div className="flex justify-center items-center h-40">
              <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
            </div>
          ) : error ? (
            <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-8">
              <div className="flex">
                <div className="flex-shrink-0">
                  <AlertCircle className="h-5 w-5 text-red-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {statsData.map((stat) => {
                const Icon = stat.icon;
                return (
                  <div key={stat.name} className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="p-5">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <Icon className={`h-6 w-6 ${stat.color}`} />
                        </div>
                        <div className="ml-5 w-0 flex-1">
                          <dl>
                            <dt className="text-sm font-medium text-gray-500 truncate">
                              {stat.name}
                            </dt>
                            <dd className="text-lg font-medium text-gray-900">
                              {stat.value}
                            </dd>
                          </dl>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Quick Actions */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  Quick Actions
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {quickActions.map((action) => {
                    const Icon = action.icon;
                    return (
                      <Link
                        key={action.name}
                        to={action.href}
                        className="relative group bg-white p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-primary-500 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors"
                      >
                        <div>
                          <span className={`${action.color} rounded-lg inline-flex p-3 text-white`}>
                            <Icon className="h-6 w-6" />
                          </span>
                        </div>
                        <div className="mt-4">
                          <h3 className="text-lg font-medium text-gray-900">
                            {action.name}
                          </h3>
                          <p className="mt-2 text-sm text-gray-500">
                            {action.description}
                          </p>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  Recent Activity
                </h3>
                <div className="flow-root">
                  <ul className="-mb-8">
                    {recentActivities.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <p>No recent activities found</p>
                      </div>
                    ) : (
                      recentActivities.map((activity) => (
                        <li key={activity.id}>
                          <div className="relative pb-8">
                            <span className="absolute left-4 top-4 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true" />
                            <div className="relative flex space-x-3">
                              <div>
                                <span className="bg-primary-500 h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white">
                                  {getActivityIcon(activity.type)}
                                </span>
                              </div>
                              <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                                <div>
                                  <p className="text-sm text-gray-900">
                                    {activity.title}
                                  </p>
                                  <p className="text-sm text-gray-500">
                                    {activity.user} • {formatTimeAgo(activity.time)}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </li>
                      ))
                    )}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Upcoming Events */}
          <div className="mt-8 bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                Upcoming Events
              </h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-4">
                  <div className="bg-primary-100 p-2 rounded-lg">
                    <Calendar className="h-5 w-5 text-primary-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">
                      Team Meeting - Q1 Review
                    </p>
                    <p className="text-sm text-gray-500">Tomorrow at 2:00 PM</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="bg-green-100 p-2 rounded-lg">
                    <Calendar className="h-5 w-5 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">
                      Product Launch Planning
                    </p>
                    <p className="text-sm text-gray-500">Friday at 10:00 AM</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="bg-orange-100 p-2 rounded-lg">
                    <Calendar className="h-5 w-5 text-orange-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">
                      Monthly All-Hands Meeting
                    </p>
                    <p className="text-sm text-gray-500">Next Monday at 9:00 AM</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <TestNotification />
    </DashboardLayout>
  );
};

export default Dashboard;